import { MenuItem, Select, TextField } from '@mui/material';
import { useMemo, useState } from 'react';
import Plot from 'react-plotly.js';
import { chartColors } from '../../helpers/chartColors';

const colorScales = [
  'RdBu',
  'Portland',
  'Picnic',
  'Hot',
  'Greys',
  'Electric',
  'Bluered',
  'Blackbody',
]

export const RTFOFiberHeatmapChart = ({ currentRTFO, monitorUWI, strainData, gridView, showSpikeLines, showLegend, darkMode, theme }) => {

  const [zValuesRange, setZValuesRange] = useState([-0.025, 0.025]);
  const [colorScale, setColorScale] = useState('RdBu');

  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];

  const data = useMemo(() => {
    return getChartData();

    function getChartData() {
      try {

        const traces = [];
        
        let X = (strainData?.dates || [])?.map(d => new Date(d)?.getTime()); // time
        let Y = strainData?.depths || []; // event depths
        let Z = strainData?.strainData || []; // strain value matrix

        traces.push(
          { // strain data 
            x: X,
            y: Y,
            z: Z,
            type: 'heatmap',
            name: 'Strain / 10 sec',
            // zmid: 0,
            zmin: zValuesRange[0],
            zmax: zValuesRange[1],
            zsmooth: 'best',
            colorscale: colorScale,
            xaxis: 'x1',
            yaxis: 'y1',
            colorbar: {
              thickness: 20, // Set the thickness (height) of the colorbar
              len: 0.65, // Set the length of the colorbar, relative to the chart size
              y: 0.3, // Set the position of the colorbar along the y-axis, 0.5 means centered
              yanchor: 'bottom', // Anchor point for the colorbar along the y-axis
              tickfont: {
                size: 10
              },
              showexponent: "all"
            },
          }
        );

        const pumpingData = resampleTimeSeries(currentStage.coldboreData, 30);
        
        const XPumping = [];
        const YPressure = [];
        const YRate = [];
        const YProppant = [];
        const YProppantBH = [];
        const YRNP = [];

        pumpingData.forEach(d => {
          XPumping.push(d.timestamp);
          YPressure.push(d.pressure);
          YRate.push(d.rate);
          YProppant.push(d.proppant);
          YProppantBH.push(d.proppantBH);
          YRNP.push(d.rnp || d.rate > 0 ? (d.pressure / d.rate * 2 / 3) : 0);
        })

        traces.push(
        { // pressure data 
          x: XPumping,
          y: YPressure,
          name: 'Pressure (MPa)',
          type: 'scatter',
          mode: 'line',
          xaxis: 'x1',
          yaxis: 'y2',
          line: {
            color: chartColors[2]
          },
          hoverinfo:"y"
        });
        traces.push(
        { // rate data 
          x: XPumping,
          y: YRate,
          name: 'Rate (m3/min)',
          type: 'scatter',
          mode: 'line',
          xaxis: 'x1',
          yaxis: 'y3',
          line: {
            color: chartColors[3]
          }
        })
        traces.push(
        { // proppant data 
          x: XPumping,
          y: YProppant,
          name: 'Proppant Surf. (kg/m3)',
          type: 'scatter',
          mode: 'line',
          xaxis: 'x1',
          yaxis: 'y4',
          line: {
            color: chartColors[4]
          }
        })
        traces.push(
        { // proppant bh data 
          x: XPumping,
          y: YProppantBH,
          name: 'Proppant BH (kg/m3)',
          type: 'scatter',
          mode: 'line',
          xaxis: 'x1',
          yaxis: 'y4',
          line: {
            color: chartColors[6]
          }
        })
        traces.push(
        { // rnp data 
          x: XPumping,
          y: YRNP,
          type: 'scatter',
          mode: 'line',
          xaxis: 'x1',
          yaxis: 'y3',
          line: {
            color: chartColors[1]
          }
        })

        return traces;
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [colorScale, currentStage.coldboreData, strainData, zValuesRange]);

  const layout = {
    
    yaxis: { // Event depth y axis
      showgrid: false,
      linecolor: darkMode ? 'lightgrey' : 'black',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: 'Fiber MD (m)',
        font: {
          size: 12
        },
      },
      autorange: 'reversed',
      rangemode: 'nonnegative',
      tickfont: {
        size: 10
      },
      domain: [0.25, 1]
    },
    yaxis2: { // Pressure y axis
      showgrid: false,
      linecolor: chartColors[2],
      color: chartColors[2],
      title: {
        text: 'Pressure (MPa)',
        font: {
          size: 12,
          standoff: 4
        },
      },
      tickfont: {
        size: 10
      },
      rangemode: 'nonnegative',
      range: [0, 100],
      domain: [0, 0.2],
      anchor: 'free',
    },
    yaxis3: { // Rate y axis
      showgrid: false,
      linecolor: chartColors[3],
      color: chartColors[3],
      title: {
        text: 'Rate (m3/min)',
        font: {
          size: 12
        },
      },
      tickfont: {
        size: 10
      },
      rangemode: 'nonnegative',
      range: [0, 20],
      domain: [0, 0.2],
      anchor: 'x1',
    },
    yaxis4: { // Proppant y axis
      showgrid: false,
      linecolor: chartColors[4],
      color: chartColors[4],
      title: {
        text: 'Conc. (kg/m3)',
        font: {
          size: 12
        },
      },
      tickfont: {
        size: 10
      },
      rangemode: 'nonnegative',
      range: [0, 1000],
      domain: [0, 0.2],
      side: 'right',
      anchor: 'x1',
    },
    xaxis: { // Perf depth x axis
      // showgrid: false,
      type: 'date',
      autorange: true,
      title: {
        text: 'Time',
        font: {
          size: 12
        }
      },
      tickfont: {
        size: 10
      },
      anchor: 'y2',
      domain: [0.1, 1],
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
    },
    hovermode: "x",
    hoverdistance: 20, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: showLegend ? true : false,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: (gridView ? -0.2 : -0.1),
    },
    annotations: [
      {
        text: "Fiber Strain Heatmap",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.13 : 1.075), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name} | Treat. Well: ${currentStage?.name?.split('_')?.[1]} | Stage: ${currentStage?.name?.split('_')?.[2]} | Fiber Well: ${monitorUWI}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.075 : 1.035), //position in y domain
        xref: 'paper',
        yref: 'paper',
      }
    ],
    shapes: [
    ],
    uirevision: true,
    margin: {"t": 60, "l": 60, "r": 100, "b": 60},
    images: [
      {
        x: (gridView ? 0.07 : 0.035),
        y: (gridView ? 1.05 : 1.025),
        sizex: (gridView ? 0.1 : 0.05),
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "y1 domain",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };


  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: `${currentRTFO?.name}-Fiber-Strain ${(new Date().toISOString().split('T')[0])}`,
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };


  return (
    <div style={{width: '100%', height: '100%', position: 'relative'}}>
      <Plot
        data={data}
        layout={layout}
        config={config}
        useResizeHandler={true}
        style={{width: '100%', height: '100%'}}
      />
      <TextField
        value={zValuesRange[1]}
        style={{
          position: 'absolute',
          top: 60,
          right: 20
        }}
        onChange={e => handleZValuesRangeInput(e, 'max')}
        InputProps={{
          style: {
            fontSize: '12px',
            height: 16,
            width: 75
          }
        }}
      />
      <TextField
        value={zValuesRange[0]}
        style={{
          position: 'absolute',
          bottom: gridView ? 160 : 280,
          right: 20
        }}
        onChange={e => handleZValuesRangeInput(e, 'min')}
        InputProps={{
          style: {
            fontSize: '12px',
            height: 16,
            width: 75
          }
        }}
      />
      <Select
        style={{
          position: 'absolute',
          top: 30,
          right: 20,
          height: 16,
          fontSize: '12px'
        }}
        value={colorScale}
        onChange={handleScaleChange}
      >
        {colorScales.map(scale => {
        return (
          <MenuItem value={scale} key={scale}>
            {scale}
          </MenuItem>
        )

        })}
      </Select>
   
    </div>
  );

  
  function handleScaleChange(e) {
    setColorScale(e.target.value);
  };

  function handleZValuesRangeInput(e, type) {
    console.log(e.target.value);
    if (type === 'min') {
      setZValuesRange(prev => [e.target.value, prev[1]])
    } else if (type === 'max') {
      setZValuesRange(prev => [prev[0], e.target.value])
    };
  };

  function resampleTimeSeries(data, targetResolution) {
    try {
  
      if (!data) return [];
  
      // Calculate the number of data points in the target resolution
      const targetDataPoints = Math.floor(data.length / targetResolution);
    
      // Create a new array to store the resampled data
      const resampledData = [];
    
      for (let i = 0; i < targetDataPoints; i++) {
        const startIndex = i * targetResolution;
        const endIndex = startIndex + targetResolution;
    
        // Extract the subset of data for the current target resolution
        const subset = data.slice(startIndex, endIndex);
    
        // Calculate the average values for the subset
        const averagePressure = subset.reduce((sum, element) => sum + element.pressure, 0) / targetResolution;
        const averageRate = subset.reduce((sum, element) => sum + element.rate, 0) / targetResolution;
        const averageProppant = subset.reduce((sum, element) => sum + element.proppant, 0) / targetResolution;
        const averageProppantBH = subset.reduce((sum, element) => sum + element.proppantBH, 0) / targetResolution;
        const averageRNP = subset.reduce((sum, element) => sum + element.rnp, 0) / targetResolution;
    
        // Calculate the timestamp for the resampled data
        const timestamp = subset[Math.floor(targetResolution / 2)].timestamp;
    
        // Create a new resampled data point
        const resampledPoint = {
          timestamp,
          pressure: averagePressure,
          rate: averageRate,
          proppant: averageProppant,
          proppantBH: averageProppantBH,
          rnp: averageRNP
        };
    
        // Push the resampled data point to the resampledData array
        resampledData.push(resampledPoint);
      };
    
      return resampledData;
  
    } catch(error) {
      console.log(error);
      return [];
    };
  };
};
